<template>
  <section class="customer-detail-summary">
    <div class="customer-detail-summary-main">
      <article class="customer-detail-card customer-detail-card--accent-primary">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-building customer-section-icon customer-section-icon--info" aria-hidden="true"></i>
            {{ text.cards.generalTitle }}
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--info"
            :title="text.actions.editGeneral"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-grid">
          <div
            v-for="item in summaryGeneral"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <button
              v-if="item.isAction"
              type="button"
              class="customer-detail-info-action"
              @click="handleSummaryItemAction(item)"
            >
              {{ item.value }}
            </button>
            <span v-else class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <PeopleContactsCard
        :contacts="summaryContacts"
        @add-contact="$emit('add-contact')"
        @edit-contact="$emit('edit-contact', $event)"
        @delete-contact="$emit('delete-contact', $event)"
      />
    </div>

    <aside class="customer-detail-summary-side">
      <ContributorBillingDataCard
        :items="summaryBillingGrid"
        :residence="summaryBillingResidence"
        @open-billing-data-list="$emit('open-billing-data-list')"
        @create-billing-data="$emit('create-billing-data')"
        @edit-billing-data="$emit('edit-billing-data')"
      />

      <article class="customer-detail-card customer-detail-card--accent-address">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-map-marker customer-section-icon customer-section-icon--address" aria-hidden="true"></i>
            {{ text.cards.addressTitle }}
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--address"
            :title="text.actions.editAddress"
            @click="$emit('edit-section', 'address')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-stack">
          <div
            v-for="item in summaryAddress"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <button
              v-if="item.isAction"
              type="button"
              class="customer-detail-info-action"
              @click="handleSummaryItemAction(item)"
            >
              {{ item.value }}
            </button>
            <span v-else class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <article class="customer-detail-card customer-detail-card--accent-warning customer-detail-card--comments">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-commenting-o customer-section-icon customer-section-icon--comments" aria-hidden="true"></i>
            {{ text.cards.commentsTitle }}
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--comments"
            :title="text.actions.editComments"
            @click="$emit('edit-section', 'comments')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-comments">
          {{ summaryComment }}
        </div>
      </article>
    </aside>
  </section>
</template>

<script>
import { buildInitials, formatCodeName, valueOrDash } from '@/utils/textValue';
import { normalizeStatusLabel } from '@/utils/dataNormalization';
import { formatDateLong } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import ContributorBillingDataCard from '@/components/contributors/ContributorBillingDataCard.vue';
import PeopleContactsCard from '@/components/people/detail/PeopleContactsCard.vue';

const { catalog: customersText } = useDomainText('customers');

export default {
  name: 'CustomerDetailInformationTab',
  components: {
    ContributorBillingDataCard,
    PeopleContactsCard,
  },
  data() {
    return {
      text: customersText.detail.informationTab,
    };
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    persons: {
      type: Array,
      default: () => [],
    },
    defaultBillingData: {
      type: Object,
      default: null,
    },
  },
  emits: ['edit-section', 'add-contact', 'edit-contact', 'delete-contact', 'edit-billing-data', 'open-billing-data-list', 'create-billing-data'],
  computed: {
    customerName() {
      return String(this.customer?.name || '').trim();
    },
    customerStatusLabel() {
      return normalizeStatusLabel(this.customer?.status ?? this.customer?.active);
    },
    summaryGeneral() {
      return [
        this.resolveSummaryItem(this.text.labels.general.name, this.customerName, 'general'),
        this.resolveSummaryItem(
          this.text.labels.general.sinceDate,
          this.resolveAdmissionDateLabel(this.customer?.since_date),
          'general'
        ),
        this.resolveSummaryItem(this.text.labels.general.website, this.customer?.website, 'general'),
        this.resolveSummaryItem(this.text.labels.general.status, this.customerStatusLabel, 'general'),
        this.resolveSummaryItem(this.text.labels.general.email, this.customer?.email, 'contact'),
        this.resolveSummaryItem(this.text.labels.general.phone, this.customer?.phone, 'contact'),
        this.resolveSummaryItem(this.text.labels.general.additionalPhone, this.customer?.fax ?? this.customer?.additionalPhone, 'contact'),
      ];
    },
    summaryBillingGrid() {
      const billingData = this.defaultBillingData || {};

      return [
        this.resolveSummaryItem(
          this.text.labels.billing.fiscalName,
          billingData?.name || billingData?.business_name || billingData?.fiscal_name,
          'billing'
        ),
        this.resolveSummaryItem(this.text.labels.billing.rfc, billingData?.rfc || this.customer?.rfc || this.customer?.tax_id, 'billing'),
        this.resolveSummaryItem(
          this.text.labels.billing.cfdiUse,
          formatCodeName(
            billingData?.uso_cfdi || this.customer?.cfdi_use || this.customer?.invoice_use,
            billingData?.uso_cfdi_name,
            billingData?.uso_cfdi_label
          ),
          'billing'
        ),
        this.resolveSummaryItem(
          this.text.labels.billing.fiscalRegime,
          formatCodeName(billingData?.regimen, billingData?.regimen_name, billingData?.regimen_label),
          'billing'
        ),
        this.resolveSummaryItem(
          this.text.labels.billing.paymentMethod,
          formatCodeName(
            billingData?.payment_method,
            billingData?.payment_method_name,
            billingData?.payment_method_label
          ),
          'billing'
        ),
        this.resolveSummaryItem(
          this.text.labels.billing.paymentMethodType,
          formatCodeName(
            billingData?.payment_method_type,
            billingData?.payment_method_type_name,
            billingData?.payment_method_type_label
          ),
          'billing'
        ),
      ];
    },
    summaryBillingResidence() {
      return this.resolveSummaryItem(this.text.labels.billing.fiscalResidence, this.buildFiscalResidence(this.defaultBillingData || {}), 'billing');
    },
    isUsingBillingAddress() {
      const rawValue = this.customer?.use_billing_address ?? this.customer?.useBillingAddress ?? 0;
      const normalized = String(rawValue ?? '').trim().toLowerCase();

      return normalized === '1' || normalized === 'true' || normalized === 'si' || normalized === 'sí';
    },
    billingAddressSource() {
      const billingData = this.defaultBillingData || {};

      const streetSegments = [
        String(billingData?.street || '').trim(),
        String(billingData?.exterior || '').trim(),
        String(billingData?.interior || '').trim(),
      ].filter(Boolean);

      const localitySegments = [
        String(billingData?.neighborhood || '').trim(),
        String(billingData?.municipality || '').trim(),
      ].filter(Boolean);

      const addressParts = [streetSegments.join(' '), ...localitySegments].filter(Boolean);

      return {
        address: addressParts.join(', '),
        country: String(billingData?.country_name || billingData?.country || '').trim(),
        state: String(billingData?.state_name || billingData?.state || '').trim(),
        city: String(billingData?.city || '').trim(),
        zipCode: String(billingData?.zipcode || '').trim(),
      };
    },
    summaryAddress() {
      if (this.isUsingBillingAddress) {
        return [
          this.resolveSummaryDisplayItem(this.text.labels.address.address, this.billingAddressSource.address, 'address'),
          this.resolveSummaryDisplayItem(this.text.labels.address.country, this.billingAddressSource.country, 'address'),
          this.resolveSummaryDisplayItem(this.text.labels.address.state, this.billingAddressSource.state, 'address'),
          this.resolveSummaryDisplayItem(this.text.labels.address.city, this.billingAddressSource.city, 'address'),
          this.resolveSummaryDisplayItem(this.text.labels.address.zipCode, this.billingAddressSource.zipCode, 'address'),
        ];
      }

      return [
        this.resolveSummaryItem(this.text.labels.address.address, this.customer?.address, 'address'),
        this.resolveSummaryItem(this.text.labels.address.country, this.customer?.country, 'address'),
        this.resolveSummaryItem(this.text.labels.address.state, this.customer?.state, 'address'),
        this.resolveSummaryItem(this.text.labels.address.city, this.customer?.city, 'address'),
        this.resolveSummaryItem(this.text.labels.address.zipCode, this.customer?.zipcode ?? this.customer?.zip_code, 'address'),
      ];
    },
    summaryComment() {
      const comment = String(this.customer?.description || this.customer?.comment || '').trim();
      return comment !== '' ? comment : this.text.comments.empty;
    },
    summaryContacts() {
      const list = Array.isArray(this.persons) ? this.persons.slice() : [];

      if (list.length === 0) {
        return [];
      }

      list.sort((left, right) => {
        const leftDefault = Number(left?.default || 0);
        const rightDefault = Number(right?.default || 0);

        if (leftDefault !== rightDefault) {
          return rightDefault - leftDefault;
        }

        const leftId = Number(left?.id || 0);
        const rightId = Number(right?.id || 0);

        return leftId - rightId;
      });

      const hasDefault = list.some((contact) => Number(contact?.default || 0) === 1);

      return list.map((contact, index) => {
        const fullName = this.resolveContactName(contact);
        const role = this.resolveContactRole(contact);
        const phone = valueOrDash(contact?.cell_phone || contact?.office_phone || contact?.home_phone || contact?.fax);

        return {
          key: `${contact?.id || index}-${fullName}`,
          name: valueOrDash(fullName),
          role,
          email: valueOrDash(contact?.email),
          phone,
          initials: buildInitials(fullName),
          isDefault: Number(contact?.default || 0) === 1 || (!hasDefault && index === 0),
          raw: contact,
        };
      });
    },
  },
  methods: {
    resolveAdmissionDateLabel(value) {
      const raw = String(value ?? '').trim().toLowerCase();
      if (
        raw === ''
        || raw === 'null'
        || raw === '0000-00-00'
        || raw === '0000-00-00 00:00:00'
      ) {
        return this.text.common.noDate;
      }

      return formatDateLong(value, { fallback: this.text.common.noDate });
    },
    resolveSummaryDisplayItem(label, value, sectionKey = 'general') {
      return {
        label,
        value: String(value ?? '').trim(),
        isAction: false,
        sectionKey,
      };
    },
    resolveSummaryItem(label, value, sectionKey = 'general') {
      const normalized = String(value ?? '').trim();

      return {
        label,
        value: normalized === '' || normalized === '-' ? this.text.common.assign : normalized,
        isAction: normalized === '' || normalized === '-',
        sectionKey,
      };
    },
    handleSummaryItemAction(item) {
      const sectionKey = String(item?.sectionKey || '').trim().toLowerCase();

      if (sectionKey === 'billing') {
        this.$emit('edit-billing-data');
        return;
      }

      this.$emit('edit-section', sectionKey || 'general');
    },
    buildFiscalResidence(billingData) {
      const zipcode = String(billingData?.zipcode || '').trim();

      const streetSegments = [
        String(billingData?.street || '').trim(),
        String(billingData?.exterior || '').trim(),
        String(billingData?.interior || '').trim(),
      ].filter(Boolean);

      const addressSegments = [
        streetSegments.join(' '),
        String(billingData?.neighborhood || '').trim(),
        String(billingData?.municipality || '').trim(),
        String(billingData?.city || '').trim(),
        String(billingData?.state_name || billingData?.state || '').trim(),
        String(billingData?.country_name || billingData?.country || '').trim(),
      ].filter(Boolean);

      const fullAddress = addressSegments.join(', ');

      if (zipcode !== '' && fullAddress !== '') {
        return `${zipcode} - ${fullAddress}`;
      }

      if (zipcode !== '') {
        return zipcode;
      }

      return fullAddress;
    },
    resolveContactName(contact) {
      const directName = String(contact?.name || '').trim();
      if (directName !== '') {
        return directName;
      }

      const firstName = String(contact?.first_name || '').trim();
      const lastName = String(contact?.last_name || '').trim();
      return `${firstName} ${lastName}`.trim();
    },
    resolveContactRole(contact) {
      const directRole = String(contact?.job_title || contact?.role || contact?.position || '').trim();
      if (directRole !== '') {
        return directRole;
      }

      const roles = [];
      if (Number(contact?.invoice_mailing || 0) === 1) {
        roles.push(this.text.contactRoles.quoteBilling);
      }

      if (Number(contact?.payments_mailing || 0) === 1) {
        roles.push(this.text.contactRoles.paymentComplements);
      }

      if (Number(contact?.is_debt_contact || 0) === 1) {
        roles.push(this.text.contactRoles.collections);
      }

      if (roles.length > 0) {
        return roles.join(' · ');
      }

      return this.text.contactRoles.noRole;
    },
  },
};
</script>
